import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Minus, Plus, ShoppingCart, PlayCircle, Download, FileText, ArrowLeft, MessageSquare, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, API, coverSrc, fmtXof, fmtDate, apiError, CATEGORY_SHORT } from "../lib/api";
import { serviceGroupOf, devGroupOf, labelOf, DEV_PLATFORMS, DEV_COMPLEXITIES, DEV_DELAYS, DEV_BUDGETS, DEV_SPECS_STATUS } from "../lib/domains";
import { useCurrency } from "../context/CurrencyContext";
import { useCart } from "../context/CartContext";
import { useQuote } from "../context/QuoteContext";
import { useAuth } from "../context/AuthContext";
import { StarRating } from "../components/StarRating";
import { Reveal } from "../components/Motion";
import { PreviewButton, QuoteButton, PromoButton } from "../components/ProductExtras";
import { isSolution, SolutionLevelBadge, SolutionModules, SolutionPricing } from "../components/SolutionExtras";
import { Price } from "../components/Price";
import { ShareProductButton } from "../components/WhatsApp";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [qty, setQty] = useState(1);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactBody, setContactBody] = useState("");
  const [contactSending, setContactSending] = useState(false);
  const { add } = useCart();
  const { add: addQuote, has: inQuote } = useQuote();
  const navigate = useNavigate();
  const { fmt } = useCurrency();
  const { user } = useAuth();

  const load = () => api.get(`/products/${id}`).then(({ data }) => setProduct(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/tiers").then(({ data }) => setTiers(data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!product) return;
    const SITE = "https://www.vitalis-store.com";
    const desc = (product.description || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 300);
    const img = coverSrc(product.cover);
    const schema = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.title,
      description: desc,
      sku: product.product_id,
      category: product.category,
      url: `${SITE}/produit/${product.product_id}`,
      brand: { "@type": "Brand", name: "Vitalis Digital Store" },
    };
    if (img) schema.image = [img.startsWith("http") ? img : `${SITE}${img}`];
    if (product.review_count > 0 && product.avg_rating > 0) {
      schema.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: product.avg_rating,
        reviewCount: product.review_count,
      };
    }
    const quoteOnlyProduct = product.quote_only || (product.category === "Développement informatique" && product.dev_quote_only);
    if (!quoteOnlyProduct && product.price > 0) {
      schema.offers = {
        "@type": "Offer",
        price: product.price,
        priceCurrency: "XOF",
        availability: "https://schema.org/InStock",
        url: `${SITE}/produit/${product.product_id}`,
      };
    }
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-schema", "product");
    el.textContent = JSON.stringify(schema);
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, [product]);

  if (!product) {
    return <div className="flex min-h-[60vh] items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-ciel" /></div>;
  }

  const isApp = product.category === "Applications informatiques";
  const isService = product.category === "Prestations de service";
  const isDev = product.category === "Développement informatique";
  const quoteOnly = product.quote_only || (isDev && product.dev_quote_only);
  const restricted = product.access_restriction === "inscrits" && !user;
  const serviceGroup = isService ? serviceGroupOf(product) : null;
  const devGroup = isDev ? devGroupOf(product) : null;
  const activeTier = isApp ? [...tiers].sort((a, b) => a.qty - b.qty).filter((t) => qty >= t.qty).pop() : null;
  const discount = activeTier ? Math.round(product.price * qty * activeTier.pct / 100) : 0;

  const submitReview = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/reviews", { product_id: product.product_id, rating, comment });
      toast.success("Merci pour votre évaluation !");
      setComment("");
      await load();
    } catch (err) {
      toast.error(apiError(err, "Évaluation impossible"));
    } finally {
      setSending(false);
    }
  };

  const openContact = () => {
    if (!user) { navigate("/auth"); return; }
    setContactOpen(true);
  };

  const sendContact = async () => {
    if (!contactBody.trim()) return;
    setContactSending(true);
    try {
      await api.post("/messages/start", { product_id: product.product_id, body: contactBody.trim() });
      toast.success("Message envoyé au vendeur. Suivez la réponse dans votre espace client.");
      setContactBody("");
      setContactOpen(false);
    } catch (err) {
      toast.error(apiError(err, "Envoi impossible"));
    } finally {
      setContactSending(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-12" data-testid="product-page">
      <Link to="/catalogue" data-testid="back-to-catalogue" className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors duration-200 hover:text-ciel">
        <ArrowLeft size={15} /> Retour au catalogue
      </Link>

      <div className="grid gap-10 lg:grid-cols-2">
        <Reveal>
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <img src={coverSrc(product.cover)} alt={product.title} className="max-h-[420px] w-full object-cover" />
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-navy px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">
              {CATEGORY_SHORT[product.category] || product.category}
            </span>
            {!isSolution(product) && <span className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-semibold text-slate-600">
              {product.product_type === "streaming"
                ? <><PlayCircle size={12} className="text-ciel" /> Vidéo en streaming illimité</>
                : <><Download size={12} className="text-ciel" /> Fichier téléchargeable (48 h, 3 usages)</>}
            </span>}
            {serviceGroup && (
              <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${serviceGroup.badge}`} data-testid="service-domain-badge">
                {serviceGroup.label}
              </span>
            )}
            {devGroup && (
              <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${devGroup.badge}`} data-testid="dev-domain-badge">
                {devGroup.label}
              </span>
            )}
            {isSolution(product) && <SolutionLevelBadge level={product.solution_level} />}
          </div>

          <h1 className="mt-4 font-display text-2xl font-bold leading-tight tracking-tight text-navy md:text-3xl" data-testid="product-title">
            {product.title}
          </h1>
          {isService && (
            <p className="mt-2 text-sm italic text-slate-500" data-testid="service-expert-mention">
              {product.service_expert
                ? `Expertise dispensée par Dr. ${product.service_expert} — Cabinet CARIS Santé, Abidjan, Côte d'Ivoire`
                : "Expertise dispensée par le Cabinet CARIS Santé — Abidjan, Côte d'Ivoire"}
            </p>
          )}
          {isDev && (
            <p className="mt-2 text-sm italic text-slate-500" data-testid="dev-ia-mention">
              Développé avec Intelligence Artificielle Générative — Cabinet CARIS Santé, Abidjan, Côte d'Ivoire
            </p>
          )}
          <div className="mt-3 flex items-center gap-2">
            <StarRating value={product.avg_rating} size={16} />
            <span className="font-mono text-sm text-slate-500" data-testid="product-rating">{product.avg_rating}/5 · {product.review_count} avis</span>
          </div>

          {product.description?.includes("<") ? (
            <div className="mt-6 leading-relaxed text-slate-600 [&_h2]:font-display [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-navy [&_li]:ml-5 [&_li]:list-disc [&_strong]:text-navy"
              data-testid="product-description" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.description || "") }} />
          ) : (
            <p className="mt-6 leading-relaxed text-slate-600" data-testid="product-description">{product.description}</p>
          )}

          {(product.presentation_video_url || product.audio_excerpt_path || (product.images || []).length > 0) && (
            <div className="mt-6 space-y-4" data-testid="product-media">
              {product.presentation_video_url && (
                /youtube\.com|youtu\.be|vimeo\.com/.test(product.presentation_video_url) ? (
                  <iframe className="aspect-video w-full rounded-xl border border-slate-200" data-testid="product-video-embed"
                    src={product.presentation_video_url
                      .replace(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/, "https://www.youtube.com/embed/$1")
                      .replace(/vimeo\.com\/(\d+)/, "https://player.vimeo.com/video/$1")}
                    allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title="Présentation" />
                ) : (
                  <video controls playsInline className="w-full rounded-xl border border-slate-200" data-testid="product-video-embed"
                    src={product.presentation_video_url.startsWith("http") ? product.presentation_video_url : `${API}/files/${product.presentation_video_url}`} />
                )
              )}
              {product.audio_excerpt_path && (
                <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="product-audio">
                  <p className="mb-2 font-mono text-xs uppercase tracking-widest text-ciel">Extrait audio</p>
                  <audio controls className="w-full" src={`${API}/files/${product.audio_excerpt_path}`} />
                </div>
              )}
              {(product.images || []).length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="product-gallery">
                  {product.images.map((img, i) => (
                    <img key={i} src={coverSrc(img)} alt="" className="h-20 w-28 rounded-lg border border-slate-200 object-cover" />
                  ))}
                </div>
              )}
            </div>
          )}

          {product.preview && (
            <div className="mt-6 rounded-xl border border-sky-100 bg-sky-50 p-5" data-testid="product-preview">
              <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-ciel"><FileText size={13} /> Aperçu gratuit</p>
              <p className="mt-2 text-sm italic leading-relaxed text-slate-600">{product.preview}</p>
            </div>
          )}

          {isSolution(product) && <><SolutionModules modules={product.solution_modules} /><SolutionPricing rows={product.solution_pricing} /></>}
          <PreviewButton product={product} />
          <PromoButton product={product} />

          {isService && (product.service_type || product.service_duration || product.service_mode || product.service_slots || product.service_places || product.service_context) && (
            <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50/60 p-5" data-testid="service-details">
              <p className="font-mono text-xs uppercase tracking-widest text-emerald-600">Détails de la prestation</p>
              <dl className="mt-3 space-y-2 text-sm">
                {product.service_type && <div className="flex gap-2"><dt className="w-44 shrink-0 text-slate-500">Type</dt><dd className="font-medium text-navy">{product.service_type}</dd></div>}
                {product.service_duration && <div className="flex gap-2"><dt className="w-44 shrink-0 text-slate-500">Durée</dt><dd className="font-medium text-navy">{product.service_duration}</dd></div>}
                {product.service_mode && <div className="flex gap-2"><dt className="w-44 shrink-0 text-slate-500">Mode de délivrance</dt><dd className="font-medium text-navy">{{ visio: "Visioconférence (lien envoyé après paiement)", presentiel: "Présentiel — Abidjan", les_deux: "Les deux" }[product.service_mode] || product.service_mode}</dd></div>}
                {product.service_slots && <div className="flex gap-2"><dt className="w-44 shrink-0 text-slate-500">Disponibilités</dt><dd className="font-medium text-navy">{product.service_slots}</dd></div>}
                {product.service_context && <div className="flex gap-2" data-testid="service-context-row"><dt className="w-44 shrink-0 text-slate-500">Contexte d'intervention</dt><dd className="font-medium text-navy">{product.service_context}</dd></div>}
                {product.service_places && <div className="flex gap-2"><dt className="w-44 shrink-0 text-slate-500">Places disponibles</dt><dd className="font-medium text-navy">{product.service_places}</dd></div>}
              </dl>
            </div>
          )}

          {isDev && (product.dev_type || product.dev_tech || product.dev_delay || product.dev_collab || product.dev_platform || product.dev_complexity || product.dev_delay_wish || product.dev_budget) && (
            <div className="mt-6 rounded-xl border border-sky-200 bg-sky-50/60 p-5" data-testid="dev-details">
              <p className="font-mono text-xs uppercase tracking-widest text-ciel">Détails du développement</p>
              <dl className="mt-3 space-y-2 text-sm">
                {product.dev_type && <div className="flex gap-2"><dt className="w-44 shrink-0 text-slate-500">Type</dt><dd className="font-medium text-navy">{product.dev_type}</dd></div>}
                {product.dev_tech && <div className="flex gap-2"><dt className="w-44 shrink-0 text-slate-500">Technologies</dt><dd className="font-medium text-navy">{product.dev_tech}</dd></div>}
                {product.dev_platform && <div className="flex gap-2" data-testid="dev-platform-row"><dt className="w-44 shrink-0 text-slate-500">Plateforme cible</dt><dd className="font-medium text-navy">{labelOf(DEV_PLATFORMS, product.dev_platform)}</dd></div>}
                {product.dev_complexity && <div className="flex gap-2" data-testid="dev-complexity-row"><dt className="w-44 shrink-0 text-slate-500">Complexité</dt><dd className="font-medium text-navy">{labelOf(DEV_COMPLEXITIES, product.dev_complexity)}</dd></div>}
                {product.dev_delay_wish && <div className="flex gap-2" data-testid="dev-delay-wish-row"><dt className="w-44 shrink-0 text-slate-500">Délai souhaité</dt><dd className="font-medium text-navy">{labelOf(DEV_DELAYS, product.dev_delay_wish)}</dd></div>}
                {product.dev_budget && <div className="flex gap-2" data-testid="dev-budget-row"><dt className="w-44 shrink-0 text-slate-500">Budget indicatif</dt><dd className="font-medium text-navy">{labelOf(DEV_BUDGETS, product.dev_budget)}</dd></div>}
                {product.dev_delay && <div className="flex gap-2"><dt className="w-44 shrink-0 text-slate-500">Délai estimé</dt><dd className="font-medium text-navy">{product.dev_delay}</dd></div>}
                {product.dev_collab && <div className="flex gap-2"><dt className="w-44 shrink-0 text-slate-500">Collaboration</dt><dd className="font-medium text-navy">{{ en_ligne: "100 % en ligne", presentiel: "Présentiel — Abidjan", hybride: "Hybride" }[product.dev_collab] || product.dev_collab}</dd></div>}
              </dl>
              {(product.dev_specs_status || product.dev_specs_required) && (
                <p className="mt-3 inline-block rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700" data-testid="dev-specs-badge">
                  Cahier des charges : {product.dev_specs_status ? labelOf(DEV_SPECS_STATUS, product.dev_specs_status) : "requis"}
                </p>
              )}
            </div>
          )}

          {product.offer_type === "prestation" && (product.milestones || []).length > 0 && (
            <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50/60 p-5" data-testid="prestation-milestones-preview">
              <p className="font-mono text-xs uppercase tracking-widest text-emerald-700">Paiement par jalons — accès progressif</p>
              <p className="mt-1 text-sm text-slate-600">Réglez cette prestation en plusieurs étapes. Chaque jalon validé débloque les livrables correspondants dans votre espace client.</p>
              <ol className="mt-3 space-y-2">
                {product.milestones.slice().sort((a, b) => (a.order || 0) - (b.order || 0)).map((m, k) => {
                  const amount = m.amount_mode === "fixed" ? m.value : Math.round((product.price || 0) * (m.value || 0) / 100);
                  return (
                    <li key={m.key || k} className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2.5 shadow-sm" data-testid={`prestation-preview-milestone-${k}`}>
                      <span className="flex items-center gap-2.5 text-sm text-slate-700">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-600 text-[11px] font-bold text-white">{k + 1}</span>
                        {m.label}
                        {m.amount_mode !== "fixed" && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">{m.value}%</span>}
                      </span>
                      <span className="font-mono text-sm font-semibold text-navy">{fmt(amount)}</span>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-end justify-between">
              <div>
                {quoteOnly ? (
                  isSolution(product) ? <div data-testid="product-price"><p className="text-[11px] uppercase tracking-wide text-slate-500">{product.solution_ttc_label ? "Abonnement mensuel" : "Total mensuel"}</p><p className="font-display text-2xl font-bold text-[#0369A1]">{product.solution_total_label}</p>{product.solution_ttc_label ? <p className="mt-1 text-xs text-slate-500">soit <b>{product.solution_ttc_label}</b> · TVA 18 % en sus</p> : <p className="mt-1 text-xs text-slate-500">Installation : <b>{product.solution_installation_label}</b></p>}{product.sales_page && <Link to={product.sales_page} data-testid="product-sales-page-link" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#0369A1] px-4 py-2 text-xs font-semibold text-white">{product.cta_label || "Demander une démo"}</Link>}</div>
                  : <p className="font-display text-xl font-bold text-amber-600" data-testid="product-price">Nous contacter pour un devis</p>
                ) : (
                  <Price xof={product.price} className="font-display text-3xl font-bold text-navy" testid="product-price" showRate />
                )}
                {isApp && <p className="mt-1 text-xs text-slate-500">par licence · remises dégressives dès 3 licences</p>}
              </div>
              {isApp && (
                <div className="flex items-center gap-3">
                  <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 text-slate-600 transition-colors duration-200 hover:border-ciel hover:text-ciel"><Minus size={14} /></button>
                  <span className="w-8 text-center font-mono text-lg font-semibold" data-testid="qty-value">{qty}</span>
                  <button data-testid="qty-plus" onClick={() => setQty(Math.min(99, qty + 1))} className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-300 text-slate-600 transition-colors duration-200 hover:border-ciel hover:text-ciel"><Plus size={14} /></button>
                </div>
              )}
            </div>

            {isApp && tiers.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="tier-badges">
                {tiers.map((t) => (
                  <span key={t.qty} className={`rounded-full px-3 py-1 font-mono text-[11px] font-semibold ${qty >= t.qty ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {t.qty}+ licences → -{t.pct}%
                  </span>
                ))}
              </div>
            )}
            {discount > 0 && (
              <p className="mt-3 text-sm font-semibold text-emerald-600" data-testid="tier-discount-info">
                Remise automatique : -{fmt(discount)} ({activeTier.pct}%)
              </p>
            )}

            {!quoteOnly && !restricted && (
            <Button
              data-testid="detail-add-to-cart"
              disabled={!!(product.available_from && new Date(product.available_from) > new Date())}
              onClick={() => add(product, qty)}
              className="mt-6 w-full rounded-full bg-ciel py-6 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
            >
              <ShoppingCart size={16} className="mr-2" />
              {product.available_from && new Date(product.available_from) > new Date()
                ? `Disponible le ${new Date(product.available_from).toLocaleDateString("fr-FR")}`
                : product.is_free ? "Ajouter au panier — Gratuit"
                : isService ? `Réserver — ${fmt(product.price * qty - discount)}`
                : isDev ? `Commander — ${fmt(product.price * qty - discount)}`
                : `Ajouter au panier — ${fmt(product.price * qty - discount)}`}
            </Button>
            )}
            {restricted && !quoteOnly && (
              <Link to="/auth" data-testid="detail-login-to-buy"
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-navy py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-navy-light">
                Connectez-vous pour acheter ce produit
              </Link>
            )}
            {quoteOnly && (
              <Button
                data-testid="detail-quote-cta"
                onClick={() => { addQuote(product); navigate("/devis"); }}
                className="mt-6 w-full rounded-full bg-navy py-6 text-sm font-semibold text-white hover:bg-slate-700"
              >
                <FileText size={16} className="mr-2" /> Demander un devis
              </Button>
            )}
            {!quoteOnly && (
              <button
                data-testid="detail-add-quote"
                onClick={() => { if (!inQuote(product.product_id)) addQuote(product); navigate("/devis"); }}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border-2 border-slate-700 px-5 py-3 text-sm font-semibold text-slate-700 transition-colors duration-200 hover:bg-slate-700 hover:text-white"
              >
                <FileText size={16} /> {inQuote(product.product_id) ? "Voir ma demande de devis" : "Demander un devis"}
              </button>
            )}
            <ShareProductButton product={product} />
            <button
              data-testid="detail-contact-vendor"
              onClick={openContact}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border-2 border-ciel px-5 py-3 text-sm font-semibold text-ciel transition-colors duration-200 hover:bg-ciel hover:text-white"
            >
              <MessageSquare size={16} /> Contacter le vendeur
            </button>
            {(isApp || isService || isDev || quoteOnly) && <QuoteButton product={product} />}
          </div>
        </Reveal>
      </div>

      {/* AVIS */}
      <section className="mt-20" data-testid="reviews-section">
        <h2 className="font-display text-2xl font-bold tracking-tight text-navy">Avis clients ({product.review_count})</h2>
        <div className="mt-8 grid gap-6 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            {product.reviews.length === 0 && <p className="text-sm text-slate-500">Aucun avis pour le moment. Soyez le premier après votre achat.</p>}
            {product.reviews.map((r) => (
              <div key={r.review_id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`review-${r.review_id}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-navy">{r.client_name}</p>
                  <span className="font-mono text-xs text-slate-400">{fmtDate(r.created_at)}</span>
                </div>
                <div className="mt-1.5"><StarRating value={r.rating} /></div>
                {r.comment && <p className="mt-2 text-sm leading-relaxed text-slate-600">{r.comment}</p>}
              </div>
            ))}
          </div>
          {user && user.role === "client" && (
            <form onSubmit={submitReview} className="h-fit rounded-xl border border-slate-200 bg-white p-6" data-testid="review-form">
              <h3 className="font-display text-base font-semibold text-navy">Évaluer ce produit</h3>
              <p className="mt-1 text-xs text-slate-500">Réservé aux acheteurs du produit.</p>
              <div className="mt-4"><StarRating value={rating} size={22} interactive onChange={setRating} /></div>
              <textarea
                data-testid="review-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                placeholder="Votre commentaire (visible publiquement)…"
                className="mt-4 w-full rounded-md border border-slate-300 bg-white p-3 text-sm outline-none focus:ring-2 focus:ring-ciel"
              />
              <Button data-testid="review-submit" disabled={sending} className="mt-4 w-full rounded-full bg-navy text-white hover:bg-navy-light">
                {sending ? "Envoi…" : "Publier mon avis"}
              </Button>
            </form>
          )}
        </div>
      </section>

      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent data-testid="contact-vendor-dialog">
          <DialogHeader>
            <DialogTitle>Contacter le vendeur</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">À propos de « {product.title} »</p>
          <Textarea
            data-testid="contact-vendor-input"
            value={contactBody}
            onChange={(e) => setContactBody(e.target.value)}
            rows={4}
            placeholder="Votre question sur ce produit…"
            className="text-sm"
          />
          <Button
            data-testid="contact-vendor-send"
            onClick={sendContact}
            disabled={contactSending || !contactBody.trim()}
            className="w-full rounded-full bg-ciel text-white hover:bg-ciel/90"
          >
            {contactSending ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
            Envoyer au vendeur
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
